import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useReportRuns, so applying both narrows rather than resets.
//
// ⚠️ Filtering is CLIENT-SIDE over the pages loaded so far. `listReportRuns`
// takes no filter argument at all (only $limit and $nextToken), so the server
// cannot narrow the result set. See useReportRuns.ts.
export type ReportRunsFilters = Record<string, string>;

let globalFilters: ReportRunsFilters = {};
const filterListeners: Set<(filters: ReportRunsFilters) => void> = new Set();

const notifyFilterChange = (filters: ReportRunsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetReportRunsFilter = () => notifyFilterChange({});

export const useReportRunsFilter = () => {
    const [filters, setFilters] = useState<ReportRunsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: ReportRunsFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
